import { useEffect, useState } from 'react';
import { GiftedEsimInterface, PlanInterface } from '../helpers/objects/planObject';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import styles from '../css/users.module.scss';
import esimModalStyles from '../css/esimAddModal.module.scss';

import { formatPlanName } from '../helpers/functions';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const EsimForPlanChooser = ({ plan, isOpen, setRefetch, onClose }: { plan: PlanInterface | null; isOpen: boolean; setRefetch: () => void; onClose: () => void }) => {
  const [esimPlans, setEsimPlans] = useState<GiftedEsimInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedEsimPlan, setSelectedEsimPlan] = useState<GiftedEsimInterface | null>(null);
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set());
  const { t } = useTranslation();
  const admin = useAuth().user;
  const getEsimPackages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${EnvPaths.SERVER_URL}/esim/get-all-packages`);
      setEsimPlans(response.data?.packages?.results ?? []);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      getEsimPackages();
    }
  }, [isOpen]);

  const handleAssingEsim = async () => {
    setLoading(true);
    try {
      await axios.patch(
        `${EnvPaths.SERVER_URL}/admin/assign-esim-to-plan`,
        {
          basePlan: plan,
          esim: selectedEsimPlan,
        },
        {
          headers: {
            Authorization: `Bearer ${admin?.token}`,
          },
        }
      );

      setRefetch();
      onClose();
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <>
      {plan ? (
        <div className={`${styles.editUserModalContainer} ${isOpen ? styles.open : ''}`}>
          <div className={`${styles.editUserModalContent}`}>
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                onClose();
              }}
              className={`${styles.closeButton}`}
            >
              <img src="/assets/images/close-icon.svg" alt="Close" />
            </button>
            <h2>{formatPlanName(t, plan?.product?.singleFlight, plan?.product?.family, plan?.product?.periodInMonths)}</h2>

            <div className={esimModalStyles.esimPackageListContainer}>
              {esimPlans?.map((esimPlan: GiftedEsimInterface) => {
                return (
                  <div
                    key={esimPlan?.package_type_id}
                    onClick={() => {
                      setSelectedEsimPlan(esimPlan);
                    }}
                    className={`${esimModalStyles.esimPackageItem} ${selectedEsimPlan?.package_type_id === esimPlan?.package_type_id ? esimModalStyles.active : ''}`}
                  >
                    <p className={esimModalStyles.esimName}>{esimPlan?.name}</p>
                    <div className={esimModalStyles.esimPackageDetailsContainer}>
                      <p>
                        Price: {esimPlan.currency_obj.symbol}
                        {esimPlan?.price}
                      </p>
                      <p>Data: {esimPlan?.data_GB} GB</p>
                      <p>Valid For (days): {esimPlan.validity_days}</p>
                      <p>
                        Supported Countries:{' '}
                        {(() => {
                          const countries = esimPlan.supported_countries;
                          const packageId = String(esimPlan.package_type_id);
                          const isExpanded = expandedCountries.has(packageId);
                          const displayedCountries = countries.length > 5 && !isExpanded ? countries.slice(0, 5) : countries;

                          return (
                            <>
                              {displayedCountries.map((country) => (
                                <span key={country.country_name}>
                                  {country.country_name} ({country.country_code}){';  '}
                                </span>
                              ))}
                              {countries.length > 5 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedCountries((prev) => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(packageId)) {
                                        newSet.delete(packageId);
                                      } else {
                                        newSet.add(packageId);
                                      }
                                      return newSet;
                                    });
                                  }}
                                  style={{
                                    marginLeft: '5px',
                                    cursor: 'pointer',
                                    background: 'none',
                                    border: 'none',
                                    color: 'inherit',
                                    textDecoration: 'underline',
                                    padding: 0,
                                    font: 'inherit',
                                  }}
                                >
                                  {isExpanded ? 'Show less' : `+${countries.length - 5} more`}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                  </div>
                );
              })}
              {loading ? (
                <div className="loader" />
              ) : (
                <button
                  className="primaryButton"
                  disabled={!selectedEsimPlan}
                  onClick={() => {
                    handleAssingEsim();
                  }}
                >
                  Select
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={`${styles.editUserModalContainer} ${isOpen ? styles.open : ''}`}></div>
      )}
    </>
  );
};

export default EsimForPlanChooser;
